import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { buildIntakeApiClient, ApiError } from "../api/client.js";
import { Field } from "../components/Field.js";
import { Select } from "../components/Select.js";
import { RequiredMark } from "../components/RequiredMark.js";
import { SegmentedToggle } from "../components/SegmentedToggle.js";
import { ErrorBanner } from "../components/ErrorBanner.js";
import { US_STATES } from "../lib/us-states.js";
import { formatPhone } from "../lib/format-phone.js";
import {
  loadGooglePlaces,
  parseAddressComponents,
  isGooglePlacesEnabled,
  type AddressComponents,
  type PlaceAutocompleteSelectEvent,
} from "../lib/google-places.js";
import {
  postBookingComplete, postBookingError, postScrollToTop, startResizeObserver,
} from "../lib/post-message.js";
import type {
  CreateBookingRequest, IntakeQueryParams, Modality,
  PatientPayload, PatientType,
} from "../api/types.js";

const DURATIONS = new Set([15, 20, 30, 45, 60]);
const MAX_RETRIES = 3;

const FormSchema = z.object({
  first_name: z.string().min(1, "Required").max(200),
  last_name:  z.string().min(1, "Required").max(200),
  dob:        z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Required (YYYY-MM-DD)"),
  gender:     z.enum(["male", "female", "nonbinary", "prefer_not_to_say"], {
                errorMap: () => ({ message: "Required" }),
              }),
  phone:      z.string().min(7, "Required").max(40),
  email:      z.string().email("Enter a valid email"),
  address_street1: z.string().min(1, "Required").max(200),
  address_street2: z.string().max(200).optional(),
  address_city:    z.string().min(1, "Required").max(100),
  address_state:   z.string().length(2, "Required"),
  address_zip:     z.string().min(3, "Required").max(20),
});
type FormValues = z.infer<typeof FormSchema>;

const PATIENT_TYPE_OPTIONS: ReadonlyArray<{ value: PatientType; label: string }> = [
  { value: "new", label: "New patient" },
  { value: "existing", label: "Existing patient" },
];

const MODALITY_OPTIONS: ReadonlyArray<{ value: Modality; label: string }> = [
  { value: "in-person", label: "In-person" },
  { value: "video", label: "Video" },
];

const MODALITY_HELP: Record<Modality, string> = {
  "in-person": "Visit our Denver clinic.",
  video: "Join from anywhere in Colorado.",
};

const GENDER_OPTIONS: ReadonlyArray<{ value: FormValues["gender"]; label: string }> = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "nonbinary", label: "Non-binary" },
  { value: "prefer_not_to_say", label: "Prefer not to say" },
];

function isModality(v: string): v is Modality {
  return v === "in-person" || v === "video";
}

export function parseQuery(search: string): IntakeQueryParams | null {
  const p = new URLSearchParams(search);
  const slotId          = p.get("slotId");
  const durationStr     = p.get("durationMinutes");
  const visitTypeSlug   = p.get("visitTypeSlug");
  const visitTypeName   = p.get("visitTypeName");
  const patientTypeRaw  = p.get("patientType");
  // The host now sends `modalities` (comma-joined). Fall back to the legacy
  // singular `modality` param for transitional safety.
  const modalitiesRaw   = p.get("modalities") ?? p.get("modality");
  const locationNameRaw = p.get("locationName");

  if (!slotId || !durationStr || !visitTypeSlug || !visitTypeName ||
      !modalitiesRaw) return null;
  const duration = Number(durationStr);
  if (!Number.isFinite(duration) || !DURATIONS.has(duration)) return null;

  // Split, trim, keep only known values, dedupe (first occurrence wins).
  const modalities: Modality[] = [];
  for (const part of modalitiesRaw.split(",")) {
    const v = part.trim();
    if (isModality(v) && !modalities.includes(v)) modalities.push(v);
  }
  if (modalities.length === 0) return null;

  // patientType is optional. When absent (or present but not a recognized
  // value) the form shows a New/Existing selector instead.
  const patientType =
    patientTypeRaw === "new" || patientTypeRaw === "existing"
      ? patientTypeRaw
      : undefined;

  // locationName is optional, for display-only purposes.
  const locationName = locationNameRaw || undefined;

  return {
    slotId, durationMinutes: duration,
    visitTypeSlug, visitTypeName,
    ...(patientType ? { patientType } : {}),
    ...(locationName ? { locationName } : {}),
    modalities,
  };
}

export function IntakePage() {
  const params = useMemo(() => parseQuery(window.location.search), []);

  useEffect(() => {
    if (!params) return;
    return startResizeObserver();
  }, [params]);

  if (!params) {
    return (
      <main className="mx-auto max-w-md p-6">
        <h1 className="text-lg font-semibold text-red-700">
          This booking link is invalid
        </h1>
        <p className="mt-2 text-sm text-gray-700">
          Please return to pricare.health and pick a new appointment time.
        </p>
      </main>
    );
  }

  return <IntakeForm params={params} />;
}

function IntakeForm({ params }: { params: IntakeQueryParams }) {
  const appointmentIdRef = useRef<string>(crypto.randomUUID());
  const api = useMemo(() => buildIntakeApiClient(), []);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  // Seeded from the URL when the embed passed patientType=; otherwise the
  // on-form New/Existing selector drives it, defaulting to "new". Always set,
  // so the booking always carries a concrete patient type.
  const [patientType, setPatientType] = useState<PatientType>(
    params.patientType ?? "new",
  );
  // The modality the patient will book under. Single-modality links use the
  // sole value; multi-modality links pre-select the first and show a chooser.
  const [modality, setModality] = useState<Modality>(params.modalities[0]!);
  const autocompleteHostRef = useRef<HTMLDivElement | null>(null);
  const [autocompleteReady, setAutocompleteReady] = useState(false);
  // Default to the autocomplete UI; user can toggle to manual entry. If
  // Places isn't configured (no API key), force manual mode — otherwise
  // the user gets stuck with no address inputs.
  const [addressMode, setAddressMode] = useState<"autocomplete" | "manual">(
    isGooglePlacesEnabled() ? "autocomplete" : "manual",
  );
  const [selectedAddress, setSelectedAddress] = useState<string | null>(null);
  const {
    register, handleSubmit, setValue, formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(FormSchema) });

  // Google Places autocomplete (New Places API — PlaceAutocompleteElement).
  // No-op when VITE_GOOGLE_MAPS_API_KEY is unset; the form falls back to a
  // plain text input for street address (handled below).
  useEffect(() => {
    let cancelled = false;
    let mountedEl: HTMLElement | null = null;
    let off: (() => void) | undefined;
    void loadGooglePlaces().then((places) => {
      if (cancelled || !places || !autocompleteHostRef.current) return;
      const ac = new places.PlaceAutocompleteElement({
        includedRegionCodes: ["us"],
      });
      // Use replaceChildren (not innerHTML = "") to clear any existing
      // children — safer DOM API that makes intent explicit and avoids
      // any chance of HTML parsing on the empty string.
      autocompleteHostRef.current.replaceChildren(ac);
      mountedEl = ac;
      setAutocompleteReady(true);

      const handler = async (event: Event) => {
        const e = event as PlaceAutocompleteSelectEvent;
        try {
          const place = e.placePrediction.toPlace();
          await place.fetchFields({ fields: ["addressComponents", "formattedAddress"] });
          const parsed: AddressComponents = parseAddressComponents(place);
          if (parsed.street1) setValue("address_street1", parsed.street1, { shouldValidate: true });
          if (parsed.city)    setValue("address_city",    parsed.city,    { shouldValidate: true });
          if (parsed.state)   setValue("address_state",   parsed.state,   { shouldValidate: true });
          if (parsed.zip)     setValue("address_zip",     parsed.zip,     { shouldValidate: true });
          if (place.formattedAddress) setSelectedAddress(place.formattedAddress);
        } catch (err) {
          // Place lookup failures should never crash the form. User can
          // still type the address manually in the fallback fields.
          console.warn("Place fetchFields failed:", err);
        }
      };
      ac.addEventListener("gmp-select", handler);
      off = () => ac.removeEventListener("gmp-select", handler);
    }).catch((err) => {
      console.warn("Google Places autocomplete unavailable:", err);
    });
    return () => {
      cancelled = true;
      off?.();
      if (mountedEl && mountedEl.parentElement) {
        mountedEl.parentElement.removeChild(mountedEl);
      }
    };
  }, [setValue]);

  const modalityLabel = modality === "video" ? "Video" : "In-person";
  const onSubmit = async (values: FormValues) => {
    setSubmitError(null);

    setSubmitting(true);
    try {
      const patient: PatientPayload = {
        first_name: values.first_name,
        last_name:  values.last_name,
        dob:        values.dob,
        gender:     values.gender,
        phone:      values.phone,
        email:      values.email,
        address_street1: values.address_street1,
        address_street2: values.address_street2 && values.address_street2.length > 0
          ? values.address_street2 : null,
        address_city:    values.address_city,
        address_state:   values.address_state,
        address_zip:     values.address_zip,
      };
      const req: CreateBookingRequest = {
        appointmentId: appointmentIdRef.current,
        slotId: params.slotId,
        visitTypeSlug: params.visitTypeSlug,
        visitTypeName: params.visitTypeName,
        patientType,
        modality,
        patient,
        // Documents are added after booking on the /documents/<token> page.
        documentIds: [],
      };

      const res = await submitWithRetry(api, req);
      setDone(true);
      postBookingComplete(res.confirmationId, res.documentsUploadUrl);
      // Bring the parent (or this iframe, if standalone) back to the top
      // so the confirmation message is what the user sees first.
      postScrollToTop();
      // Embedded: the parent website owns the documents prompt. Standalone:
      // send the patient straight to the upload page.
      // ?booked= lets the documents page show the confirmation.
      if (window.parent === window && res.documentsUploadUrl) {
        const next = new URL(res.documentsUploadUrl, window.location.href);
        next.searchParams.set("booked", res.confirmationId);
        window.location.assign(next.toString());
      }
    } catch (err) {
      const message = mapErrorToUserMessage(err);
      // Terminal 4xx, or a network error / 5xx after submitWithRetry gave up:
      // either way the booking failed, so tell the parent (its Retry UI).
      postBookingError(message, err instanceof ApiError ? err.code : undefined);
      setSubmitError(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <main className="mx-auto max-w-md p-6 text-center">
        <p className="text-sm text-gray-700">Redirecting…</p>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-2xl p-6">
      <header className="mb-6">
        <h1 className="text-xl font-semibold">
          Booking a {params.visitTypeName}
        </h1>
        <p className="mt-1 text-sm text-gray-700">
          {params.durationMinutes}-minute visit ·{" "}
          <span className="inline-block rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-800">
            {modalityLabel}
          </span>
          {params.locationName ? (
            <>
              {" "}· <span>{params.locationName}</span>
            </>
          ) : null}
        </p>
      </header>

      {submitError ? <ErrorBanner message={submitError} /> : null}

      <form
        onSubmit={handleSubmit(onSubmit, (errs) => {
          // If the user is in autocomplete mode and any address field
          // failed validation, reveal the manual fields so they can
          // see + fix the errors. Otherwise the inline errors render
          // inside hidden containers and are invisible.
          if (
            addressMode === "autocomplete" &&
            (errs.address_street1 ||
              errs.address_city ||
              errs.address_state ||
              errs.address_zip)
          ) {
            setAddressMode("manual");
          }
        })}
        className="grid gap-4"
      >
        <p className="text-xs text-slate-500">
          Fields marked with <span aria-hidden="true" className="text-red-600">*</span> are required.
        </p>
        {(params.patientType == null || params.modalities.length > 1) ? (
          <div className="flex flex-wrap items-start gap-6">
            {params.patientType == null ? (
              <SegmentedToggle<PatientType>
                label="Are you a new or existing patient?"
                ariaLabel="New or existing patient"
                options={PATIENT_TYPE_OPTIONS}
                value={patientType}
                onChange={setPatientType}
                required
              />
            ) : null}
            {params.modalities.length > 1 ? (
              <div>
                <SegmentedToggle<Modality>
                  label="How would you like to be seen?"
                  ariaLabel="How would you like to be seen?"
                  options={params.modalities.map(
                    (m) => MODALITY_OPTIONS.find((o) => o.value === m)!,
                  )}
                  value={modality}
                  onChange={setModality}
                  required
                />
                <p className="mt-1 text-xs text-slate-500">
                  {MODALITY_HELP[modality]}
                </p>
              </div>
            ) : null}
          </div>
        ) : null}
        <div className="grid grid-cols-2 gap-4">
          <Field label="First name" required autoComplete="given-name"
            {...register("first_name")}
            error={errors.first_name?.message} />
          <Field label="Last name" required autoComplete="family-name"
            {...register("last_name")}
            error={errors.last_name?.message} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Date of birth" type="date" required autoComplete="bday"
            {...register("dob")}
            error={errors.dob?.message} />
          <Select
            label="Gender"
            required
            options={GENDER_OPTIONS}
            placeholder="Select…"
            {...register("gender")}
            error={errors.gender?.message}
          />
        </div>
        <Field
          label="Phone"
          type="tel"
          required
          inputMode="tel"
          autoComplete="tel"
          placeholder="(555) 123-4567"
          {...register("phone", {
            onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
              e.target.value = formatPhone(e.target.value);
            },
          })}
          error={errors.phone?.message}
        />
        <Field label="Email" type="email" required autoComplete="email"
          {...register("email")}
          error={errors.email?.message} />
        {isGooglePlacesEnabled() && addressMode === "autocomplete" ? (
          <div>
            <label
              htmlFor="place-autocomplete-host"
              className="block text-sm font-medium text-slate-700"
            >
              Address
              <RequiredMark />
            </label>
            <div
              id="place-autocomplete-host"
              ref={autocompleteHostRef}
              className="place-autocomplete-host mt-1"
            />
            {selectedAddress ? (
              <p className="mt-1 text-xs text-emerald-700">
                ✓ {selectedAddress}
              </p>
            ) : !autocompleteReady ? (
              <p className="mt-1 text-xs text-slate-500">Loading address search…</p>
            ) : (
              <p className="mt-1 text-xs text-slate-500">
                Start typing your address and pick from the dropdown.
              </p>
            )}
            <button
              type="button"
              onClick={() => setAddressMode("manual")}
              className="mt-2 text-xs font-medium text-blue-600 hover:text-blue-700 hover:underline"
            >
              Enter address manually
            </button>
          </div>
        ) : null}

        {/* Manual address fields. Always rendered so react-hook-form keeps
            them registered (and so values populated by the autocomplete
            survive a mode toggle); visibility is toggled via the `hidden`
            class. */}
        <div className={addressMode === "manual" ? "grid gap-4" : "hidden"}>
          <Field
            label="Street address"
            required
            autoComplete="street-address"
            {...register("address_street1")}
            error={errors.address_street1?.message}
          />
          <Field label="Apt / Suite (optional)" autoComplete="address-line2"
            {...register("address_street2")} />
          <div className="grid grid-cols-3 gap-4">
            <Field label="City" required autoComplete="address-level2"
              {...register("address_city")}
              error={errors.address_city?.message} />
            <Select label="State" required autoComplete="address-level1"
              options={US_STATES}
              placeholder="State…"
              {...register("address_state")}
              error={errors.address_state?.message} />
            <Field label="Zip" required autoComplete="postal-code"
              {...register("address_zip")}
              error={errors.address_zip?.message} />
          </div>
          {isGooglePlacesEnabled() ? (
            <button
              type="button"
              onClick={() => setAddressMode("autocomplete")}
              className="-mt-2 self-start text-xs font-medium text-blue-600 hover:text-blue-700 hover:underline"
            >
              Use address search instead
            </button>
          ) : null}
        </div>

        <div className="mt-2 flex justify-end">
          <button
            type="submit"
            disabled={submitting}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow disabled:opacity-50"
          >
            {submitting ? "Submitting…" : "Confirm booking"}
          </button>
        </div>
      </form>
    </main>
  );
}

async function submitWithRetry(
  api: ReturnType<typeof buildIntakeApiClient>,
  req: CreateBookingRequest,
) {
  let lastErr: unknown;
  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    try {
      return await api.createBooking(req);
    } catch (err) {
      lastErr = err;
      if (isTerminal(err)) throw err;
      await new Promise((r) => setTimeout(r, 250 * (attempt + 1)));
    }
  }
  throw lastErr;
}

function isTerminal(err: unknown): boolean {
  if (!(err instanceof ApiError)) return false;
  if (err.status === 0) return false;            // network — transient
  if (err.status >= 500 && err.status < 600) return false;
  return true;
}

function mapErrorToUserMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === "slot_token_expired" || err.code === "slot_token_invalid") {
      return "This time slot is no longer available — please return to pricare.health and pick a new time.";
    }
    if (err.code === "legacy_slot_token") {
      // The slot came from the retired no-location availability path; the
      // API refuses it so nothing is booked outside the admin schedules.
      return "This time is no longer bookable — please return to pricare.health and choose a location and time again.";
    }
    if (err.code === "slot_taken") {
      return "This time slot was just booked by someone else — please return to pricare.health and pick a new time.";
    }
    if (err.code === "documents_invalid") {
      return "There was a problem with the uploaded photos. Please try again.";
    }
  }
  return "We couldn't process your booking. Please try again or call us.";
}
